<script setup>
import { ref, inject, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';
import TierSelect from '../TierSelect.vue';
import NivelDeRaciocinio from '../NivelDeRaciocinio.vue';
import { POWER_AGENT_MODELS, TIERS, rotuloCurto } from '../modelCatalog';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');

const saving = ref(false);

// Qual modelo atende o agente é informação de PLATAFORMA: o produto vende faixa, não
// fornecedor. A seção inteira é só de super admin (`plataforma` em AgentPage) e o servidor
// nem entrega o modelo a mais ninguém. O prompt, que morava aqui, virou a seção Prompt em
// 23/09/2026: o cliente via uma seção "Modelo" com só o prompt dentro.
const modelPrimary = ref(props.agent.config?.model_primary || TIERS[0].primary);
// Agente com modelo e sem reserva fica sem reserva: preencher com a da faixa padrão fazia o
// resumo dizer que ela assume, e o Salvar a gravaria sem ninguém ter escolhido.
const modelFallback = ref(
  props.agent.config?.model_fallback ||
    (props.agent.config?.model_primary ? '' : TIERS[0].fallback)
);
const catalogo = ref(POWER_AGENT_MODELS);

// Quanto o modelo pensa antes de responder (`reasoning_effort`). '' é o padrão do modelo, que
// vai como null e apaga a chave. Agente novo já nasce com 'none' quando o par aceita.
const raciocinio = ref(props.agent.config?.reasoning_effort || '');
const avisoRaciocinio = ref(false);

// O catálogo vivo diz quem deixa desligar o raciocínio (`reasoning_mandatory`). Sem ele (rede
// fora, ou modelo personalizado fora da curadoria), a tela não bloqueia nada e o servidor
// confere — ele recusa `none` com o nome do modelo.
onMounted(async () => {
  const vivo = await powerAgentsApi.models(ctx.accountId());
  if (vivo.length) catalogo.value = vivo;
});

const modeloQueObrigaRaciocinio = computed(() => {
  const obrigado = [modelPrimary.value, modelFallback.value]
    .map(id => catalogo.value.find(m => m.id === id))
    .find(m => m?.reasoning_mandatory === true);
  return obrigado ? rotuloCurto(obrigado.label) || obrigado.id : null;
});

// Trocar para um par que não deixa desligar volta o raciocínio para o padrão, e diz isso: salvar
// o par com 'none' seria recusado, e a pessoa não saberia por quê.
watch(modeloQueObrigaRaciocinio, modelo => {
  if (modelo && raciocinio.value === 'none') {
    raciocinio.value = '';
    avisoRaciocinio.value = true;
  }
});

// A frase-resumo diz o que vai ao ar em linguagem de gente: quem responde, quem assume e quanto
// pensa. Ela muda enquanto se escolhe, então quem salva sabe o que está salvando.
const nomeDoModelo = id =>
  rotuloCurto(catalogo.value.find(m => m.id === id)?.label) || id;
const nomeDoNivel = computed(() => ({
  '': t('POWER_AGENTS.REASONING.DEFAULT'),
  none: t('POWER_AGENTS.REASONING.OPTIONS.NONE'),
  low: t('POWER_AGENTS.REASONING.OPTIONS.LOW'),
  medium: t('POWER_AGENTS.REASONING.OPTIONS.MEDIUM'),
  high: t('POWER_AGENTS.REASONING.OPTIONS.HIGH'),
}));
const resumo = computed(() => {
  const partes = {
    principal: nomeDoModelo(modelPrimary.value),
    reserva: nomeDoModelo(modelFallback.value),
    nivel: nomeDoNivel.value[raciocinio.value] || raciocinio.value,
  };
  return modelFallback.value
    ? t('POWER_AGENTS.MODEL_SECTION.SUMMARY', partes)
    : t('POWER_AGENTS.MODEL_SECTION.SUMMARY_NO_FALLBACK', partes);
});

function escolherTier(opcao) {
  modelPrimary.value = opcao.primary;
  modelFallback.value = opcao.fallback;
}

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  modelPrimary: modelPrimary.value,
  modelFallback: modelFallback.value,
  raciocinio: raciocinio.value,
}));

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        // Sem reserva, a chave não vai: o PATCH faz merge raso e nada é inventado.
        config: {
          model_primary: modelPrimary.value,
          ...(modelFallback.value && { model_fallback: modelFallback.value }),
          reasoning_effort: raciocinio.value || null,
        },
      }
    );
    avisoRaciocinio.value = false;
    ctx.onSectionSaved(updated);
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);
</script>

<template>
  <SectionShell
    :title="t('POWER_AGENTS.MODEL_SECTION.TITLE')"
    :description="t('POWER_AGENTS.MODEL_SECTION.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <p
      aria-live="polite"
      data-resumo
      class="flex items-start gap-2 px-3 py-2.5 text-sm rounded-lg bg-n-alpha-1 text-n-slate-12"
    >
      <i class="i-lucide-cpu size-4 mt-0.5 shrink-0 text-n-slate-11" />
      {{ resumo }}
    </p>

    <div data-busca="model" class="flex flex-col gap-3">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('POWER_AGENTS.MODELS.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.MODELS.DESCRIPTION') }}
        </p>
      </div>
      <TierSelect
        :primary="modelPrimary"
        :fallback="modelFallback"
        :catalog="catalogo"
        @pick="escolherTier"
      />
    </div>

    <div
      data-busca="reasoning"
      class="flex flex-col gap-3 pt-5 border-0 border-t border-solid border-n-weak"
    >
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('POWER_AGENTS.REASONING.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.REASONING.DESCRIPTION') }}
        </p>
      </div>
      <NivelDeRaciocinio
        v-model="raciocinio"
        :bloqueado-por="modeloQueObrigaRaciocinio"
        :voltou-ao-padrao="avisoRaciocinio"
      />
    </div>
  </SectionShell>
</template>
